import isFunction from 'lodash/isFunction';
import Logger from '@Services/Logger';
import MarvinImageExporter from '@Services/MarvinImageExporter';
import { MarvinConfig } from '@Constants/MarvinConstants';
import MarvinHelpers from '@Services/MarvinHelpers';
import StructureEditorInterface from '../StructureEditorInterface';
import { CDDOptions } from './ChemdrawConstants';

function hasReactions(reactions) {
  return Array.isArray(reactions) && reactions.length > 0;
}

async function getReactionData(drawing) {
  if (!drawing?.findReactions || !drawing?.getRXNV3000) {
    return '';
  }

  try {
    const reactions = await drawing.findReactions();
    if (!hasReactions(reactions)) {
      return '';
    }

    const reactionData = await drawing.getRXNV3000();
    return MarvinHelpers.isReaction(reactionData) ? reactionData : '';
  } catch (error) {
    Logger.warn('ChemDraw reaction detection/export failed, falling back to MOL export:', error);
    return '';
  }
}

function getAttachOptions(container, license, onContentChange) {
  return {
    license,
    chemistryServiceURL: CDDOptions.chemistryServiceURL,
    editor: {
      element: container,
      layout: 'full',
      canvasMode: 'infinite',
      onContentChange,
      features: {
        templates: true,
        extendedCopyPaste: true,
        chemicalWarnings: true,
        hideLogo: true,
        reactionMapping: {
          alwaysShow: true,
          automatic: false,
        },
      },
    },
  };
}

export default class ChemdrawStructureEditor extends StructureEditorInterface {
  /**
   * @ngdoc function
   * @name constructor
   * @description constructor
   */
  constructor(container) {
    super();
    this._instance = null;
    this.state = this._ready(container);
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name _ready
   * @param {HTMLElement} container
   * @returns {Promise}
   * @description initialize Chemdraw editor in DOM
   */
  async _ready(container) {
    try {
      // Check if RevvitySignals is available
      if (!globalThis.RevvitySignals?.ChemDrawJS?.attach) {
        throw new Error('RevvitySignals.ChemDrawJS is not available');
      }

      // Fetch the license
      const response = await fetch(CDDOptions.licenseUrl);
      if (!response.ok) {
        throw new Error(`Failed to load ChemDraw license: ${response.status}`);
      }
      const license = await response.text();

      const notifyChange = async () => {
        if (isFunction(this.onChangeCallback)) {
          const isEditorEmpty = await this.isEmpty();
          this.onChangeCallback(isEditorEmpty);
        }
      };

      const { editor, chemistryService } = await globalThis.RevvitySignals.ChemDrawJS.attach(
        getAttachOptions(container, license, notifyChange)
      );

      this._instance = editor;
      if (!chemistryService) {
        Logger.warn(
          'ChemDraw chemistry service is unavailable — structure-to-name and other features will not work.',
          'Check that the ChemDraw Web Service is running at:',
          CDDOptions.chemistryServiceURL
        );
      }

      if (this._instance?.drawing && 'onContentChange' in this._instance.drawing) {
        this._instance.drawing.onContentChange = notifyChange;
      } else if (this._instance && isFunction(this._instance.setContentChangedHandler)) {
        this._instance.setContentChangedHandler(notifyChange);
      }

      if (isFunction(this.onLoadedCallback)) {
        this.onLoadedCallback();
      }
      return editor;
    } catch (error) {
      Logger.error('Could not attach ChemDraw JS', error.message);
      throw error;
    }
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name _loadMol
   * @param {String} data
   * @returns {Promise}
   * @description load mol data into Chemdraw editor
   */
  async _loadMol(data) {
    if (!this._instance) {
      throw new Error('Missing Chemdraw instance');
    }

    const setMolResult = this._instance?.drawing?.setMOL?.(data);
    if (setMolResult instanceof Promise) {
      await setMolResult;
    }

    // Trigger onChangeCallback after structure was loaded into editor
    if (isFunction(this.onChangeCallback)) {
      const isEditorEmpty = await this.isEmpty();
      this.onChangeCallback(isEditorEmpty);
    }
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name _export
   * @returns {Promise}
   * @description get mol data from Chemdraw editor
   */
  async _export() {
    if (!this._instance) {
      throw new Error('Missing Chemdraw instance');
    }

    try {
      // Check if editor is empty first
      const isEmpty = await this._instance.drawing.isEmpty();
      if (isEmpty) {
        Logger.info('Editor is empty, returning empty string');
        return '';
      }

      const reactionData = await getReactionData(this._instance?.drawing);
      if (reactionData) {
        return reactionData;
      }

      if (this._instance?.drawing?.getMOLV3000) {
        const molData = await this._instance.drawing.getMOLV3000();
        if (molData) {
          return molData;
        }
      }

      throw new Error('getMOLV3000 method not available on drawing API');
    } catch (error) {
      Logger.error('Export failed:', error);
      throw error;
    }
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name _preview
   * @param {String} moldData
   * @returns {Promise}
   * @description get preview image
   */
  _preview(moldData) {
    if (this._instance?.drawing?.getImageURL) {
      return this._instance.drawing.getImageURL();
    }

    return MarvinImageExporter.render({
      data: moldData,
      format: MarvinHelpers.isReaction(moldData) ? MarvinConfig.FORMATS.RXNV3 : MarvinConfig.FORMATS.MOL,
    });
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name dispose
   * @description dispose current editor instance
   */
  dispose() {
    try {
      if (this._instance) {
        // Clean up content changed handler
        if (this._instance?.drawing && 'onContentChange' in this._instance.drawing) {
          this._instance.drawing.onContentChange = null;
        } else if (isFunction(this._instance.setContentChangedHandler)) {
          this._instance.setContentChangedHandler(null);
        }

        // Dispose/detach the editor instance
        if (isFunction(this._instance.detach)) {
          this._instance.detach();
        } else if (isFunction(this._instance.dispose)) {
          this._instance.dispose();
        }

        this._instance = null;
      }

      // Clear callbacks
      this.onChangeCallback = null;
      this.onLoadedCallback = null;
    } catch (error) {
      Logger.error('Error disposing ChemDraw editor:', error);
    }
  }

  // ------- Shared API -------

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name getData
   * @returns {Promise}
   * @description get current structure data, including mol data and preview image
   */
  async getData() {
    try {
      await this.state;
      const isEmpty = await this.isEmpty();
      if (isEmpty) {
        return {
          data: '',
          preview: null,
        };
      }

      const molData = await this._export();

      // Validate molData before trying to generate preview
      if (!molData || (typeof molData === 'string' && molData.trim() === '')) {
        return {
          data: molData || '',
          preview: null,
        };
      }

      const preview = await this._preview(molData);
      const result = {
        data: molData,
        preview,
      };

      return result;
    } catch (error) {
      Logger.error('getData error:', error);
      throw error;
    }
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name loadStructure
   * @param {String} structure
   * @returns {Promise}
   * @description load structure (mold data) into current editor
   */
  loadStructure(structure) {
    return this.state.then(() => this._loadMol(structure)).catch(Logger.reject);
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name isEmpty
   * @returns {Promise}
   * @description check if current editor is empty
   */
  async isEmpty() {
    if (!this._instance?.drawing) {
      Logger.info('isEmpty: no instance or drawing, returning true');
      return true;
    }
    try {
      const result = await this._instance.drawing.isEmpty();
      return Boolean(result);
    } catch (error) {
      Logger.warn('Error checking if editor is empty:', error);
      return true;
    }
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name clear
   * @description clears current editor
   */
  async clear() {
    if (this._instance?.drawing) {
      try {
        await this._instance.drawing.clear?.();

        // Trigger onChangeCallback after clearing
        if (isFunction(this.onChangeCallback)) {
          const isEditorEmpty = await this.isEmpty();
          this.onChangeCallback(isEditorEmpty);
        }
      } catch (error) {
        Logger.error('Error clearing ChemDraw editor:', error);
      }
    }
  }

  onChange(callback) {
    this.onChangeCallback = callback;
  }

  onLoaded(callback) {
    this.onLoadedCallback = callback;
  }
}
